import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import './Admin.css';

// Helper function to compress images before uploading
const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.75) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Canvas compression failed'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const Admin = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [images, setImages] = useState([]);
  const [fetchingImages, setFetchingImages] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });
  
  // Drag and drop states for images
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Drag and drop states for categories
  const [draggedCatIndex, setDraggedCatIndex] = useState(null);
  const [dragOverCatIndex, setDragOverCatIndex] = useState(null);

  // Category CRUD states
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [newCategoryTitle, setNewCategoryTitle] = useState('');
  const [newCategoryTitleEn, setNewCategoryTitleEn] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryTitle, setEditingCategoryTitle] = useState('');
  const [editingCategoryTitleEn, setEditingCategoryTitleEn] = useState('');

  const navigate = useNavigate();

  // Check auth session
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/admin/login');
      } else {
        setSession(session);
        await fetchCategories();
      }
      setLoading(false);
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
        navigate('/admin/login');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Fetch all categories
  const fetchCategories = async () => {
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        setCategories(data);
        const keys = data.map(c => c.key);
        setSelectedSection((prev) => {
          if (!prev || !keys.includes(prev)) {
            return data[0].key;
          }
          return prev;
        });
      } else {
        setCategories([]);
        setSelectedSection('');
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
      setSelectedSection('');
    }
  };

  // Fetch images for selected section
  const fetchImages = useCallback(async (sectionKey) => {
    if (!supabase || !sectionKey) return;
    setFetchingImages(true);
    setStatusMsg({ type: '', text: '' });
    try {
      const { data, error } = await supabase
        .from('section_images')
        .select('*')
        .eq('section_key', sectionKey)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      setStatusMsg({ type: 'error', text: 'Không thể tải danh sách ảnh: ' + error.message });
    } finally {
      setFetchingImages(false);
    }
  }, []);

  // Fetch images when section changes
  useEffect(() => {
    if (session && supabase && selectedSection) {
      fetchImages(selectedSection);
    }
  }, [selectedSection, session, fetchImages]);

  // Handle Logout
  const handleLogout = async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  // Add new dynamic Category
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryTitle.trim() || !supabase) return;

    const slugify = (text) => {
      return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '') + '-section';
    };

    const catKey = slugify(newCategoryTitle.trim()) + `-${Date.now().toString().slice(-4)}`;
    const nextSortOrder = categories.length > 0 
      ? Math.max(...categories.map(c => c.sort_order || 0)) + 1 
      : 0;

    try {
      const payload = {
        key: catKey,
        title: newCategoryTitle.trim(),
        title_en: newCategoryTitleEn.trim() || null,
        sort_order: nextSortOrder
      };

      const { error } = await supabase
        .from('categories')
        .insert([payload]);

      if (error) throw error;
      
      setNewCategoryTitle('');
      setNewCategoryTitleEn('');
      setShowAddCategoryForm(false);
      setStatusMsg({ type: 'success', text: 'Thêm mục mới thành công!' });
      await fetchCategories();
      setSelectedSection(catKey);
    } catch (err) {
      console.error('Error adding category:', err);
      setStatusMsg({ type: 'error', text: 'Thêm mục thất bại: ' + err.message });
    }
  };

  // Rename existing Category
  const handleRenameCategory = async (catId, catKey) => {
    if (!editingCategoryTitle.trim() || !supabase) return;

    try {
      const payload = {
        title: editingCategoryTitle.trim(),
        title_en: editingCategoryTitleEn.trim() || null
      };

      const { error } = await supabase
        .from('categories')
        .update(payload)
        .eq('id', catId);

      if (error) throw error;

      setStatusMsg({ type: 'success', text: 'Cập nhật danh mục thành công!' });
      setEditingCategoryId(null);
      setEditingCategoryTitle('');
      setEditingCategoryTitleEn('');
      await fetchCategories();
    } catch (err) {
      console.error('Error renaming category:', err);
      setStatusMsg({ type: 'error', text: 'Đổi tên danh mục thất bại: ' + err.message });
    }
  };

  // Delete Category and cascade-delete all its images
  const handleDeleteCategory = async (catId, catKey, catTitle) => {
    if (categories.length <= 1) {
      alert('Không thể xóa danh mục cuối cùng. Cần giữ lại ít nhất một mục!');
      return;
    }
    if (!window.confirm(`Bạn có chắc chắn muốn xóa mục "${catTitle}"? Tất cả hình ảnh trong mục này cũng sẽ bị xóa khỏi bộ lưu trữ đám mây!`)) return;

    try {
      const { data: catImgs, error: fetchErr } = await supabase
        .from('section_images')
        .select('image_path, image_path_en')
        .eq('section_key', catKey);

      if (fetchErr) throw fetchErr;

      if (catImgs && catImgs.length > 0) {
        const filePaths = [];
        catImgs.forEach(img => {
          if (img.image_path) filePaths.push(img.image_path);
          if (img.image_path_en) filePaths.push(img.image_path_en);
        });

        if (filePaths.length > 0) {
          const { error: storageErr } = await supabase.storage
            .from('images')
            .remove(filePaths);
          if (storageErr) console.warn('Storage delete warning:', storageErr);
        }
      }

      const { error: imgDelErr } = await supabase
        .from('section_images')
        .delete()
        .eq('section_key', catKey);
      if (imgDelErr) throw imgDelErr;

      const { error: catDelErr } = await supabase
        .from('categories')
        .delete()
        .eq('id', catId);
      if (catDelErr) throw catDelErr;

      setStatusMsg({ type: 'success', text: `Đã xóa mục "${catTitle}" và toàn bộ ảnh của mục này.` });
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setStatusMsg({ type: 'error', text: 'Xóa danh mục thất bại: ' + err.message });
    }
  };

  // Handle New Image Upload
  const handleUploadNewImage = async (e) => {
    if (!supabase || !selectedSection) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setStatusMsg({ type: 'error', text: 'Vui lòng chọn file ảnh hợp lệ.' });
      return;
    }

    setUploading(true);
    setStatusMsg({ type: 'info', text: 'Đang nén tối ưu hóa ảnh...' });

    try {
      const compressedFile = await compressImage(file, 1200, 1200, 0.75);
      const fileName = `vi_${Date.now()}_${Math.random().toString(36).substring(2, 9)}.jpg`;
      const filePath = `${selectedSection}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, compressedFile, { contentType: 'image/jpeg' });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      const nextSortOrder = images.length > 0 
        ? Math.max(...images.map(img => img.sort_order || 0)) + 1 
        : 0;

      const { error: dbError } = await supabase
        .from('section_images')
        .insert([
          {
            section_key: selectedSection,
            image_url: publicUrl,
            image_path: filePath,
            sort_order: nextSortOrder
          }
        ]);

      if (dbError) throw dbError;

      setStatusMsg({ type: 'success', text: 'Tải ảnh mới thành công!' });
      fetchImages(selectedSection);
    } catch (error) {
      console.error('Upload error:', error);
      setStatusMsg({ type: 'error', text: 'Tải ảnh thất bại: ' + error.message });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Upload or Replace specific Image slot (VI or EN)
  const handleUploadImageSlot = async (imgItem, e, langType = 'vi') => {
    if (!supabase || !imgItem) return;
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setStatusMsg({ type: 'error', text: 'Vui lòng chọn file ảnh hợp lệ.' });
      return;
    }

    setUploading(true);
    const langLabel = langType === 'vi' ? 'Tiếng Việt' : 'Tiếng Anh';
    setStatusMsg({ type: 'info', text: `Đang tải ảnh ${langLabel}...` });

    try {
      const compressedFile = await compressImage(file, 1200, 1200, 0.75);
      const fileName = `${langType}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}.jpg`;
      const filePath = `${selectedSection}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, compressedFile, { contentType: 'image/jpeg' });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      const oldPath = langType === 'vi' ? imgItem.image_path : imgItem.image_path_en;
      if (oldPath) {
        await supabase.storage.from('images').remove([oldPath]);
      }

      const updates = langType === 'vi'
        ? { image_url: publicUrl, image_path: filePath }
        : { image_url_en: publicUrl, image_path_en: filePath };

      const { error: dbError } = await supabase
        .from('section_images')
        .update(updates)
        .eq('id', imgItem.id);

      if (dbError) throw dbError;

      setStatusMsg({ type: 'success', text: `Cập nhật ảnh ${langLabel} thành công!` });
      fetchImages(selectedSection);
    } catch (error) {
      console.error('Upload slot error:', error);
      setStatusMsg({ type: 'error', text: `Tải ảnh ${langLabel} thất bại: ` + error.message });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Remove English image
  const handleRemoveEnImage = async (imgItem) => {
    if (!supabase || !imgItem.image_path_en) return;
    if (!window.confirm('Bạn có chắc muốn xóa riêng ảnh tiếng Anh của mục này không?')) return;

    try {
      await supabase.storage.from('images').remove([imgItem.image_path_en]);

      const { error } = await supabase
        .from('section_images')
        .update({ image_url_en: null, image_path_en: null })
        .eq('id', imgItem.id);

      if (error) throw error;

      setStatusMsg({ type: 'success', text: 'Đã xóa ảnh tiếng Anh của mục này!' });
      fetchImages(selectedSection);
    } catch (error) {
      console.error('Remove EN image error:', error);
      setStatusMsg({ type: 'error', text: 'Xóa ảnh tiếng Anh thất bại: ' + error.message });
    }
  };

  // Delete image item
  const handleDeleteItem = async (imgItem) => {
    if (!supabase) return;
    if (!window.confirm('Bạn có chắc chắn muốn xóa toàn bộ mục ảnh này không (gồm cả ảnh TV và TA)?')) return;

    setStatusMsg({ type: '', text: '' });

    try {
      const pathsToRemove = [];
      if (imgItem.image_path) pathsToRemove.push(imgItem.image_path);
      if (imgItem.image_path_en) pathsToRemove.push(imgItem.image_path_en);

      if (pathsToRemove.length > 0) {
        await supabase.storage.from('images').remove(pathsToRemove);
      }

      const { error: dbError } = await supabase
        .from('section_images')
        .delete()
        .eq('id', imgItem.id);

      if (dbError) throw dbError;

      setStatusMsg({ type: 'success', text: 'Đã xóa mục ảnh thành công!' });
      fetchImages(selectedSection);
    } catch (error) {
      console.error('Delete error:', error);
      setStatusMsg({ type: 'error', text: 'Xóa mục ảnh thất bại: ' + error.message });
    }
  };

  // Handle Reordering of images
  const updateOrderInDb = async (reorderedList) => {
    if (!supabase) return;
    try {
      const updates = reorderedList.map((img, index) => ({
        id: img.id,
        section_key: img.section_key,
        image_url: img.image_url,
        image_path: img.image_path,
        image_url_en: img.image_url_en || null,
        image_path_en: img.image_path_en || null,
        sort_order: index,
        created_at: img.created_at
      }));

      const { error } = await supabase
        .from('section_images')
        .upsert(updates);

      if (error) throw error;
      setStatusMsg({ type: 'success', text: 'Đã cập nhật thứ tự ảnh thành công!' });
    } catch (err) {
      console.error('Error updating order:', err);
      setStatusMsg({ type: 'error', text: 'Cập nhật thứ tự thất bại: ' + err.message });
      fetchImages(selectedSection);
    }
  };

  const reorderImages = async (startIndex, endIndex) => {
    if (startIndex === endIndex) return;

    const reordered = Array.from(images);
    const [removed] = reordered.splice(startIndex, 1);
    reordered.splice(endIndex, 0, removed);

    setImages(reordered);
    await updateOrderInDb(reordered);
  };

  // Handle Reordering of categories
  const reorderCategories = async (startIndex, endIndex) => {
    if (startIndex === endIndex || !supabase) return;

    const reordered = Array.from(categories);
    const [removed] = reordered.splice(startIndex, 1);
    reordered.splice(endIndex, 0, removed);

    setCategories(reordered);

    try {
      const updates = reordered.map((cat, index) => ({
        id: cat.id,
        key: cat.key,
        title: cat.title,
        title_en: cat.title_en || null,
        sort_order: index,
        created_at: cat.created_at
      }));

      const { error } = await supabase
        .from('categories')
        .upsert(updates);

      if (error) throw error;
      setStatusMsg({ type: 'success', text: 'Đã lưu thứ tự danh mục mới!' });
    } catch (err) {
      console.error('Error reordering categories:', err);
      setStatusMsg({ type: 'error', text: 'Sắp xếp danh mục thất bại: ' + err.message });
      await fetchCategories();
    }
  };

  if (!supabase) {
    return (
      <div className="admin-loading-screen">
        <div style={{ background: '#1e293b', padding: '40px', borderRadius: '16px', maxWidth: '500px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 style={{ color: '#fca5a5', marginBottom: '16px' }}>Chưa Cấu Hình Supabase</h2>
          <p style={{ color: '#94a3b8', lineHeight: '1.6', textAlign: 'left' }}>
            Vui lòng điền thông tin <strong>REACT_APP_SUPABASE_URL</strong> và <strong>REACT_APP_SUPABASE_ANON_KEY</strong> vào file <strong>.env</strong>.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-loading-screen">
        <div className="admin-spinner"></div>
        <p>Đang kiểm tra phiên đăng nhập...</p>
      </div>
    );
  }

  const currentCategory = categories.find(s => s.key === selectedSection);

  return (
    <div className="admin-dashboard-container">
      {/* Header bar */}
      <header className="admin-header">
        <div className="admin-header-title-wrapper">
          <button 
            className="admin-menu-toggle-btn"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle Sidebar Menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <h1 className="admin-header-title">Finn Website Admin</h1>
          <span className="admin-user-badge">{session?.user?.email}</span>
        </div>
        <button onClick={handleLogout} className="admin-logout-btn">
          Đăng Xuất
        </button>
      </header>

      {/* Main workspace layout */}
      <div className="admin-main">
        {/* Mobile Sidebar backdrop */}
        {isMobileMenuOpen && (
          <div className="admin-sidebar-backdrop" onClick={() => setIsMobileMenuOpen(false)}></div>
        )}

        {/* Left navigation sidebar */}
        <aside className={`admin-sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="admin-sidebar-header">
            <h3>Các mục trang chủ</h3>
            <div className="admin-sidebar-header-actions">
              <button 
                className="admin-add-cat-toggle-btn"
                onClick={() => setShowAddCategoryForm(!showAddCategoryForm)}
                title="Thêm danh mục mới"
              >
                +
              </button>
              <button 
                className="admin-sidebar-close-btn"
                onClick={() => setIsMobileMenuOpen(false)}
                title="Đóng menu"
              >
                ✕
              </button>
            </div>
          </div>

          {session?.user?.email && (
            <div className="admin-sidebar-user-info-mobile">
              <span className="admin-sidebar-email-label">Tài khoản:</span>
              <span className="admin-sidebar-email">{session.user.email}</span>
            </div>
          )}

          {showAddCategoryForm && (
            <form onSubmit={handleAddCategory} className="admin-add-cat-form">
              <input
                type="text"
                placeholder="Tên Tiếng Việt (ví dụ: Liên hệ)..."
                value={newCategoryTitle}
                onChange={(e) => setNewCategoryTitle(e.target.value)}
                required
                autoFocus
              />
              <input
                type="text"
                placeholder="Tên Tiếng Anh (English Title)..."
                value={newCategoryTitleEn}
                onChange={(e) => setNewCategoryTitleEn(e.target.value)}
              />
              <div className="admin-add-cat-form-btns">
                <button type="submit" className="admin-add-cat-save">Lưu</button>
                <button 
                  type="button" 
                  className="admin-add-cat-cancel" 
                  onClick={() => {
                    setShowAddCategoryForm(false);
                    setNewCategoryTitle('');
                    setNewCategoryTitleEn('');
                  }}
                >
                  Hủy
                </button>
              </div>
            </form>
          )}

          <ul className="admin-sidebar-menu">
            {categories.map((cat, index) => (
              <li 
                key={cat.key || cat.id}
                className={`admin-sidebar-item ${selectedSection === cat.key ? 'active' : ''} ${draggedCatIndex === index ? 'dragging' : ''} ${dragOverCatIndex === index ? 'drag-over' : ''}`}
                draggable="true"
                onDragStart={(e) => {
                  setDraggedCatIndex(index);
                  e.dataTransfer.setData("text/category", index.toString());
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragOverCatIndex !== index) {
                    setDragOverCatIndex(index);
                  }
                }}
                onDragEnd={() => {
                  setDraggedCatIndex(null);
                  setDragOverCatIndex(null);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const fromIdxStr = e.dataTransfer.getData("text/category");
                  if (fromIdxStr !== "") {
                    const fromIdx = parseInt(fromIdxStr, 10);
                    reorderCategories(fromIdx, index);
                  }
                  setDraggedCatIndex(null);
                  setDragOverCatIndex(null);
                }}
              >
                <span className="admin-cat-drag-handle">⋮⋮</span>
                
                {editingCategoryId === cat.id ? (
                  <div className="admin-cat-edit-wrapper-dual">
                    <input
                      type="text"
                      placeholder="Tiếng Việt"
                      value={editingCategoryTitle}
                      onChange={(e) => setEditingCategoryTitle(e.target.value)}
                      required
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <input
                      type="text"
                      placeholder="Tiếng Anh (EN)"
                      value={editingCategoryTitleEn}
                      onChange={(e) => setEditingCategoryTitleEn(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="admin-cat-edit-btns">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRenameCategory(cat.id, cat.key);
                        }}
                        className="admin-cat-edit-save"
                        title="Lưu"
                      >
                        ✓
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCategoryId(null);
                          setEditingCategoryTitle('');
                          setEditingCategoryTitleEn('');
                        }}
                        className="admin-cat-edit-cancel"
                        title="Hủy"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="admin-cat-view-wrapper" 
                    onClick={() => {
                      setSelectedSection(cat.key);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <div className="admin-cat-title-box">
                      <span className="admin-cat-title">{cat.title}</span>
                      {cat.title_en && <span className="admin-cat-title-en">EN: {cat.title_en}</span>}
                    </div>
                    <div className="admin-cat-actions">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCategoryId(cat.id);
                          setEditingCategoryTitle(cat.title || '');
                          setEditingCategoryTitleEn(cat.title_en || '');
                        }}
                        className="admin-cat-rename-btn"
                        title="Đổi tên danh mục"
                      >
                        ✏️
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(cat.id, cat.key, cat.title);
                        }}
                        className="admin-cat-delete-btn"
                        title="Xóa danh mục"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </aside>

        {/* Right workspace details */}
        <main className="admin-content-area">
          {/* Status alerts */}
          {statusMsg.text && (
            <div className={`admin-alert ${statusMsg.type}`}>
              {statusMsg.text}
            </div>
          )}

          <div className="admin-content-header">
            <div>
              <h2>Quản lý ảnh: {currentCategory?.title || 'Chọn danh mục'}</h2>
              {currentCategory?.title_en && (
                <span className="admin-subhead-en">Tên Tiếng Anh: {currentCategory.title_en}</span>
              )}
            </div>

            <div className="admin-upload-actions">
              <label htmlFor="image-upload-input" className={`admin-upload-btn ${uploading || !selectedSection ? 'disabled' : ''}`}>
                {uploading ? (
                  <>
                    <span className="admin-inline-spinner"></span> Đang tải...
                  </>
                ) : (
                  '+ Tải mục ảnh mới'
                )}
              </label>
              <input
                id="image-upload-input"
                type="file"
                accept="image/*"
                onChange={handleUploadNewImage}
                disabled={uploading || !selectedSection}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {!selectedSection ? (
            <div className="admin-empty-state">
              <p>Vui lòng chọn hoặc thêm danh mục mới bên thanh menu trái để bắt đầu quản lý ảnh.</p>
            </div>
          ) : fetchingImages ? (
            <div className="admin-content-loading">
              <div className="admin-spinner"></div>
              <p>Đang tải danh sách ảnh...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="admin-empty-state">
              <p>Mục này hiện chưa có ảnh tải lên trong Database.</p>
              <p className="admin-empty-subtext">Hãy tải ảnh mới lên bằng nút "+ Tải mục ảnh mới" ở góc trên bên phải.</p>
            </div>
          ) : (
            <div className="admin-dual-images-list">
              {images.map((img, index) => (
                <div 
                  key={img.id} 
                  className={`admin-dual-card ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                  draggable="true"
                  onDragStart={(e) => {
                    setDraggedIndex(index);
                    e.dataTransfer.setData("text/plain", index.toString());
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (dragOverIndex !== index) {
                      setDragOverIndex(index);
                    }
                  }}
                  onDragEnd={() => {
                    setDraggedIndex(null);
                    setDragOverIndex(null);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const fromIndexStr = e.dataTransfer.getData("text/plain");
                    if (fromIndexStr !== "") {
                      const fromIndex = parseInt(fromIndexStr, 10);
                      reorderImages(fromIndex, index);
                    }
                    setDraggedIndex(null);
                    setDragOverIndex(null);
                  }}
                >
                  <div className="admin-dual-card-header">
                    <div className="admin-dual-card-title">
                      <span className="admin-drag-handle" title="Kéo thả để sắp xếp">⋮⋮</span>
                      <span className="admin-dual-card-index">Mục ảnh #{index + 1}</span>
                    </div>
                    <button
                      onClick={() => handleDeleteItem(img)}
                      className="admin-dual-card-delete-btn"
                      title="Xóa mục ảnh này (Cả TV & TA)"
                    >
                      Xóa Mục Này 🗑️
                    </button>
                  </div>

                  <div className="admin-dual-slots-container">
                    {/* Vietnamese Image Slot */}
                    <div className="admin-slot-box vi-slot">
                      <div className="admin-slot-badge vi-badge">🇻🇳 Ảnh Tiếng Việt</div>
                      <div className="admin-slot-img-wrapper">
                        <img src={img.image_url} alt={`VI ${index + 1}`} loading="lazy" decoding="async" />
                      </div>
                      <div className="admin-slot-actions">
                        <label htmlFor={`upload-vi-${img.id}`} className="admin-slot-btn btn-change">
                          Thay Ảnh TV
                        </label>
                        <input
                          id={`upload-vi-${img.id}`}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleUploadImageSlot(img, e, 'vi')}
                          disabled={uploading}
                          style={{ display: 'none' }}
                        />
                      </div>
                    </div>

                    {/* English Image Slot */}
                    <div className="admin-slot-box en-slot">
                      <div className="admin-slot-badge en-badge">🇬🇧 Ảnh Tiếng Anh</div>
                      <div className="admin-slot-img-wrapper">
                        {img.image_url_en ? (
                          <img src={img.image_url_en} alt={`EN ${index + 1}`} loading="lazy" decoding="async" />
                        ) : (
                          <div className="admin-slot-empty">
                            <span className="admin-slot-empty-icon">🖼️</span>
                            <span className="admin-slot-empty-text">Chưa có ảnh tiếng Anh</span>
                            <span className="admin-slot-empty-subtext">(Sẽ tự động hiển thị ảnh TV)</span>
                          </div>
                        )}
                      </div>
                      <div className="admin-slot-actions">
                        <label htmlFor={`upload-en-${img.id}`} className="admin-slot-btn btn-change-en">
                          {img.image_url_en ? 'Thay Ảnh TA' : '+ Thêm Ảnh TA'}
                        </label>
                        <input
                          id={`upload-en-${img.id}`}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleUploadImageSlot(img, e, 'en')}
                          disabled={uploading}
                          style={{ display: 'none' }}
                        />
                        {img.image_url_en && (
                          <button
                            onClick={() => handleRemoveEnImage(img)}
                            className="admin-slot-btn btn-remove-en"
                            title="Xóa riêng ảnh tiếng Anh"
                          >
                            Xóa Ảnh TA
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Admin;
